import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Users, User, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { relativeTime, absoluteTime } from "@/lib/format";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "player", label: "Player" },
  { key: "team", label: "Team" },
];

const CAT_COLORS = {
  injury: "#FFB020",
  suspension: "#F87171",
  trade: "#38BDF8",
  lineup: "#00E5A3",
  goalie: "#A855F7",
};

function AlertRow({ a, onRead }) {
  const isTeam = a.team_alert === true;
  const cat = a.category || "update";
  const color = CAT_COLORS[cat] || "#94A3B8";
  return (
    <div data-testid={`alert-row-${a.id}`}
      className={`flex items-start gap-3 px-4 py-3.5 border-b border-[#1A2133] transition-colors ${a.read ? "opacity-55" : "bg-[#0F1420]"}`}>
      {!a.read && <span className="mt-2 h-2 w-2 rounded-full bg-[#00E5A3] shrink-0" data-testid={`alert-unread-dot-${a.id}`} />}
      {a.read && <span className="mt-2 h-2 w-2 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ color, background: `${color}1a` }}>{cat}</span>
          <span data-testid={`alert-scope-${a.id}`}
            className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded flex items-center gap-1 ${isTeam ? "text-[#38BDF8] bg-[#38BDF8]/10" : "text-[#00E5A3] bg-[#00E5A3]/10"}`}>
            {isTeam ? <Users size={9} /> : <User size={9} />}{isTeam ? "Team" : "Player"}
          </span>
          {a.league && <span className="text-[9px] font-mono text-slate-500">{a.league}</span>}
          <span className="ml-auto text-[10px] font-mono text-slate-500" title={absoluteTime(a.created_at)}>{relativeTime(a.created_at)}</span>
        </div>
        <Link to={a.player_id ? `/players/${a.player_id}` : "#"} className="text-sm text-slate-100 leading-snug hover:text-[#00E5A3]">
          {a.headline}
        </Link>
        {a.source && <p className="text-[10px] font-mono text-slate-600 mt-1">{a.source}</p>}
      </div>
      {!a.read && (
        <button data-testid={`alert-read-btn-${a.id}`} onClick={() => onRead(a.id)}
          className="text-[11px] font-mono text-[#00E5A3] hover:underline shrink-0 mt-1">Mark read</button>
      )}
    </div>
  );
}

export default function AlertCenter() {
  const { user } = useAuth();
  const [data, setData] = useState({ alerts: [], unread: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/alerts");
      setData(r.data || { alerts: [], unread: 0 });
    } catch (e) { console.warn("[AlertCenter] failed to load alerts:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); else setLoading(false); }, [user, load]);

  const markRead = async (id) => {
    await api.put(`/alerts/${id}/read`);
    setData((d) => ({ ...d, alerts: d.alerts.map((a) => a.id === id ? { ...a, read: true } : a), unread: Math.max(0, d.unread - 1) }));
  };
  const markAll = async () => { await api.post("/alerts/read-all"); load(); };

  const alerts = (data.alerts || []).filter((a) => {
    if (filter === "unread") return !a.read;
    if (filter === "player") return a.team_alert !== true;
    if (filter === "team") return a.team_alert === true;
    return true;
  });

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <Bell size={32} className="mx-auto text-slate-600 mb-3" />
        <p className="text-slate-300">Sign in to see your alerts.</p>
        <Link to="/login" className="text-[#00E5A3] hover:underline text-sm mt-2 inline-block">Log in →</Link>
      </div>
    );
  }

  return (
    <div data-testid="alert-center-page" className="max-w-[820px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 flex items-center gap-2">
          <Bell size={22} className="text-[#00E5A3]" /> Alert Center
        </h1>
        {data.unread > 0 && (
          <button data-testid="alert-center-mark-all" onClick={markAll}
            className="text-xs text-[#00E5A3] hover:underline flex items-center gap-1">
            <Check size={13} /> Mark all read
          </button>
        )}
      </div>
      <p className="text-sm text-slate-400 mb-4">
        Every status change on the players and teams you follow. {data.unread > 0 ? `${data.unread} unread.` : "You're all caught up."}
      </p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={13} className="text-slate-500" />
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`alert-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === f.key ? "bg-[#38BDF8] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
            {f.label}{f.key === "unread" && data.unread > 0 ? ` (${data.unread})` : ""}
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-[#0B0F18] border border-[#232D42] overflow-hidden">
        {loading ? (
          <div className="px-4 py-16 text-center text-slate-500 text-sm">Loading…</div>
        ) : alerts.length === 0 ? (
          <div data-testid="alert-center-empty" className="px-4 py-16 text-center text-slate-500 text-sm">
            {filter === "all"
              ? <>No alerts yet. Add players to your <Link to="/watchlist" className="text-[#00E5A3] hover:underline">watchlist</Link> to start getting them.</>
              : `No ${filter} alerts.`}
          </div>
        ) : (
          alerts.map((a) => <AlertRow key={a.id} a={a} onRead={markRead} />)
        )}
      </div>
    </div>
  );
}
